import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { Separator } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import { CustomIcons } from "@/components/icons/CustomIcons";
import {
  useCreatePrescriptionTemplate,
  useDeletePrescriptionTemplate,
  usePrescriptionTemplates,
  useUpdatePrescriptionTemplate,
  type PrescriptionTemplateWithItems,
} from "@/hooks/usePrescriptionTemplates";
import { useMedications } from "@/hooks/useMedications";
import { ManipulatedPrescriptionForm } from "@/components/prescriptions/ManipulatedPrescriptionForm";
import { CreateMedicationDialog } from "@/components/prescriptions/CreateMedicationDialog";
import type { Database } from "@/integrations/supabase/types";
import {
  manipulatedPayloadSchema,
  type ManipulatedPayloadInput,
} from "@/lib/validations/medical-prescription";
import type { MedicationWithRelations } from "@/components/prescriptions/MedicationAutocomplete";
import {
  prescriptionTemplateSchema,
  type PrescriptionTemplateInput,
} from "@/lib/validations/prescription-template";
import { cn } from "@/lib/utils";
import { useCurrentClinicId } from "@/hooks/useAppointments";

type MedicationRow = Database["public"]["Tables"]["medications"]["Row"];

const DEFAULT_MANIPULATED_PAYLOAD: ManipulatedPayloadInput = {
  components: [
    {
      medication_id: "",
      name: "",
      concentration: "",
    },
  ],
  pharmaceutical_form: "",
  treatment_days: "",
  total_doses: "",
  route: "",
};

const buildDefaultValues = (): PrescriptionTemplateInput => ({
  title: "",
  observations: "",
  template_type: "manipulated",
  items: [],
  manipulated_payload: structuredClone(DEFAULT_MANIPULATED_PAYLOAD),
});

const parseManipulatedPayload = (payload: unknown): ManipulatedPayloadInput => {
  const parsed = manipulatedPayloadSchema.safeParse(payload);
  if (parsed.success) return parsed.data;
  return structuredClone(DEFAULT_MANIPULATED_PAYLOAD);
};

const getComponentNames = (payload: unknown): string[] => {
  if (!payload || typeof payload !== "object") return [];
  const components = (payload as { components?: unknown }).components;
  if (!Array.isArray(components)) return [];
  return components
    .map((component) => {
      if (!component || typeof component !== "object") return "";
      const name = (component as { name?: unknown }).name;
      return typeof name === "string" ? name : "";
    })
    .filter((name) => name.trim().length > 0);
};

interface ManipulatedPrescriptionTemplatesTabProps {
  selectedTemplateId?: string | null;
  onTemplateApplied?: () => void;
  showTemplatesList?: boolean;
}

export const ManipulatedPrescriptionTemplatesTab = ({
  selectedTemplateId,
  onTemplateApplied,
  showTemplatesList = true,
}: ManipulatedPrescriptionTemplatesTabProps) => {
  const { t } = useTranslation("prescriptions");
  const { data: clinicId } = useCurrentClinicId();

  const {
    data: templates = [],
    isLoading: isLoadingTemplates,
  } = usePrescriptionTemplates(clinicId, "manipulated");

  const createTemplate = useCreatePrescriptionTemplate();
  const updateTemplate = useUpdatePrescriptionTemplate();
  const deleteTemplate = useDeletePrescriptionTemplate();

  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<PrescriptionTemplateWithItems | null>(null);
  const [medicationSearch, setMedicationSearch] = useState("");
  const [showCreateMedicationDialog, setShowCreateMedicationDialog] = useState(false);
  const [medicationNameToCreate, setMedicationNameToCreate] = useState("");
  const [medicationCreateIndex, setMedicationCreateIndex] = useState<number | null>(null);

  const form = useForm<PrescriptionTemplateInput>({
    resolver: zodResolver(prescriptionTemplateSchema),
    defaultValues: buildDefaultValues(),
  });

  const { data: medications = [], isLoading: isLoadingMedications } = useMedications({
    searchTerm: medicationSearch,
    includeControlled: true,
  });

  const resetForm = () => {
    form.reset(buildDefaultValues());
    setEditingTemplateId(null);
    setMedicationSearch("");
  };

  const handleEditTemplate = useCallback((template: PrescriptionTemplateWithItems) => {
    form.reset({
      title: template.title ?? "",
      observations: template.observations ?? "",
      template_type: "manipulated",
      items: [],
      manipulated_payload: parseManipulatedPayload(template.manipulated_payload),
    });
    setEditingTemplateId(template.id);
    setMedicationSearch("");
  }, [form]);

  useEffect(() => {
    if (!selectedTemplateId) return;
    const template = templates.find((item) => item.id === selectedTemplateId);
    if (!template) return;
    handleEditTemplate(template);
    onTemplateApplied?.();
  }, [handleEditTemplate, selectedTemplateId, templates, onTemplateApplied]);

  const handleSubmit = async (values: PrescriptionTemplateInput) => {
    if (!clinicId) return;

    const payload: PrescriptionTemplateInput = {
      ...values,
      template_type: "manipulated",
      items: [],
    };

    if (editingTemplateId) {
      await updateTemplate.mutateAsync({
        templateId: editingTemplateId,
        clinicId,
        payload,
      });
    } else {
      await createTemplate.mutateAsync({ ...payload, clinic_id: clinicId });
    }

    resetForm();
  };

  const handleOpenCreateMedicationDialog = (name: string, index: number) => {
    setMedicationNameToCreate(name);
    setMedicationCreateIndex(index);
    setShowCreateMedicationDialog(true);
  };

  const handleMedicationCreated = (medication: MedicationRow) => {
    if (medicationCreateIndex === null) return;
    form.setValue(`manipulated_payload.components.${medicationCreateIndex}.medication_id`, medication.id);
    form.setValue(`manipulated_payload.components.${medicationCreateIndex}.name`, medication.name);
    form.clearErrors(`manipulated_payload.components.${medicationCreateIndex}.name`);
    setMedicationSearch("");
    setMedicationCreateIndex(null);
  };

  const activeTemplate = useMemo(
    () => templates.find((template) => template.id === editingTemplateId) ?? null,
    [templates, editingTemplateId],
  );

  return (
    <div className={cn(showTemplatesList ? "grid gap-6 md:grid-cols-[2fr,1fr] md:items-start" : "space-y-6")}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 order-last md:order-first">
          <Card>
            <CardHeader>
              <CardTitle>{t("templates.manipulatedTitle")}</CardTitle>
              <CardDescription>{t("templates.manipulatedDescription")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 mb-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("templates.templateTitle")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("templates.manipulatedTitlePlaceholder")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <ManipulatedPrescriptionForm
                form={form as unknown as Parameters<typeof ManipulatedPrescriptionForm>[0]["form"]}
                medications={medications as MedicationWithRelations[]}
                isLoadingMedications={isLoadingMedications}
                onSearchChange={setMedicationSearch}
                onOpenCreateDialog={handleOpenCreateMedicationDialog}
              />

              <FormField
                control={form.control}
                name="observations"
                render={({ field }) => (
                  <FormItem className="mt-4">
                    <FormLabel>{t("templates.complementaryGuidance")}</FormLabel>
                    <FormControl>
                      <Textarea rows={3} placeholder={t("templates.complementaryGuidancePlaceholder")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            {editingTemplateId ? (
              <Button
                type="button"
                variant="outline"
                className="shadow-soft-search"
                onClick={resetForm}
              >
                <CustomIcons name="cancel-circle" className="!h-6 !w-6 !text-color-dark" />
                {t("templates.actions.cancelEdit")}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="shadow-soft-search"
                onClick={resetForm}
                disabled={!clinicId || createTemplate.isPending || updateTemplate.isPending}
              >
                <CustomIcons name="cancel-circle" className="!h-6 !w-6 !text-color-dark" />
                {t("templates.actions.clear")}
              </Button>
            )}

            <Button
              type="submit"
              className="w-full sm:w-auto"
              disabled={!clinicId || createTemplate.isPending || updateTemplate.isPending}
            >
              {(createTemplate.isPending || updateTemplate.isPending) ? (
                <Loader2 className="!h-5 !w-5 animate-spin" />
              ) : (
                <CustomIcons name="check-circle" className="!h-6 !w-6 !text-primary-foreground" />
              )}
              <span className="font-normal">
                {editingTemplateId ? t("templates.actions.update") : t("templates.actions.save")}
              </span>
            </Button>
          </div>
        </form>
      </Form>

      {showTemplatesList && (
        <Card className="order-first md:order-last">
          <CardHeader>
            <CardTitle>{t("templates.registeredTemplates")}</CardTitle>
            {activeTemplate && (
              <CardDescription>
                {t("templates.actions.editing", { title: activeTemplate.title })}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4 md:max-h-[500px]">
            {isLoadingTemplates ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("composer.loadingTemplates")}
              </div>
            ) : templates.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("templates.empty.manipulated")}
              </p>
            ) : (
              <div className="space-y-3 text-sm">
                {templates.map((template) => {
                  const componentNames = getComponentNames(template.manipulated_payload);
                  return (
                    <div
                      key={template.id}
                      className="rounded-md border border-border/70 p-3"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                          <div className="font-medium">{template.title}</div>
                          <Badge variant="secondary">{t("composer.templateTagManipulated")}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t("templates.counts.components", { count: componentNames.length })}
                        </div>
                      </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditTemplate(template)}
                            aria-label={t("templates.editTemplate")}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setTemplateToDelete(template)}
                            aria-label={t("templates.deleteTemplate")}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {componentNames.length > 0 && (
                        <>
                          <Separator className="my-2" />
                          <ul className="space-y-1 text-xs text-muted-foreground">
                            {componentNames.slice(0, 3).map((name, index) => (
                              <li key={index}>{name}</li>
                            ))}
                          </ul>
                          {componentNames.length > 3 && (
                            <p className="mt-1 text-xs text-muted-foreground">
                              {t("templates.counts.moreItems", { count: componentNames.length - 3 })}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {templateToDelete && (
        <ConfirmationDialog
          open={!!templateToDelete}
          onOpenChange={(open) => {
            if (!open) setTemplateToDelete(null);
          }}
          title={t("templates.actions.deleteTitle")}
          description={t("templates.actions.deleteDescription", { title: templateToDelete.title })}
          confirmText={t("templates.actions.delete")}
          cancelText={t("templates.actions.cancel")}
          variant="destructive"
          onConfirm={() => {
            if (!templateToDelete || !clinicId) return;
            deleteTemplate.mutate({ templateId: templateToDelete.id, clinicId });
            setTemplateToDelete(null);
          }}
        />
      )}

      <CreateMedicationDialog
        open={showCreateMedicationDialog}
        onOpenChange={(open) => {
          setShowCreateMedicationDialog(open);
          if (!open) setMedicationCreateIndex(null);
        }}
        initialName={medicationNameToCreate}
        onMedicationCreated={handleMedicationCreated}
      />
    </div>
  );
};
